// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ScrollIndicator } from '@/components/scroll-indicator';
import { TimelineAxis, TimelineAxisDensity } from '@/components/timeline-axis';
import { TimelineColumns } from '@/components/timeline-columns';
import { TimelineControls } from '@/components/timeline-controls';
import { TimelineDateIndicator } from '@/components/timeline-date-indicator';
import { TimelineProvider } from '@/contexts/timeline-provider';
import { useResizeObserver } from '@/hooks/use-resize-observer';
import { useScrollIndicator } from '@/hooks/use-scroll-indicator';
import { useTimelineState } from '@/hooks/use-timeline-state';
import { Project } from '@/project/api/types';
import { ProjectTimelineSegment } from '@/project/components/project-timeline-segment';
import { ProjectTimelineTooltip } from '@/project/components/project-timeline-tooltip';
import { getAssignedAssetCount } from '@/project/utilities/get-assigned-asset-count';
import { getRequestedAssetCount } from '@/project/utilities/get-requested-asset-count';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import {
  Alert,
  Box,
  Collapse,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Link } from '@tanstack/react-router';
import { localPoint } from '@visx/event';
import { scaleTime } from '@visx/scale';
import { defaultStyles, useTooltip, useTooltipInPortal } from '@visx/tooltip';
import { DateTime } from 'luxon';
import { MouseEvent, MouseEventHandler, useRef } from 'react';

const AXIS_HEIGHT = 40;
const BAR_HEIGHT = 30;
const BAR_MARGIN = 8;
const DEFAULT_DENSITY: TimelineAxisDensity = 'month';

function compareProjectsByStart(project: Project, other: Project) {
  if (project.startOn === null || other.startOn === null) {
    return 0;
  }

  return project.startOn.toMillis() - other.startOn.toMillis();
}

function compareProjectsByEnd(project: Project, other: Project) {
  if (project.endOn === null || other.endOn === null) {
    return 0;
  }

  return project.endOn.toMillis() - other.endOn.toMillis();
}

function compareProjectsByName(project: Project, other: Project) {
  return project.name.localeCompare(other.name);
}

function compareProjectsByAssets(project: Project, other: Project) {
  const projectRequestedAssetCount = getRequestedAssetCount(project);
  const otherRequestedAssetCount = getRequestedAssetCount(other);

  if (projectRequestedAssetCount === otherRequestedAssetCount) {
    const projectAssignedAssetCount = getAssignedAssetCount(project);
    const otherAssignedAssetCount = getAssignedAssetCount(other);

    return projectAssignedAssetCount - otherAssignedAssetCount;
  }

  return projectRequestedAssetCount - otherRequestedAssetCount;
}

function compareProjects(project: Project, other: Project) {
  const startComparison = compareProjectsByStart(project, other);

  if (startComparison !== 0) {
    return startComparison;
  }

  const endComparison = compareProjectsByEnd(project, other);

  if (endComparison !== 0) {
    return endComparison;
  }

  const nameComparison = compareProjectsByName(project, other);

  if (nameComparison !== 0) {
    return nameComparison;
  }

  return compareProjectsByAssets(project, other);
}

interface Props {
  /**
   * A collection of projects to render in the timeline.
   */
  projects: Project[];

  /**
   * When true, display a loading skeleton timeline.
   */
  isLoading?: boolean;
}

/**
 * A timeline that displays a collection of projects as segments over an
 * interval. Projects without a start or end date are not displayed.
 */
export function ProjectTimeline(props: Props) {
  const today = DateTime.now().startOf('day');

  const theme = useTheme();
  const svgRef = useRef<SVGElement | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const resizeObserver = useResizeObserver(ref);
  const scrollIndicator = useScrollIndicator();
  const timelineState = useTimelineState(resizeObserver.width, DEFAULT_DENSITY);

  const { projects, isLoading = false } = props;

  const {
    tooltipTop = 0,
    tooltipLeft = 0,
    tooltipData,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip<Project>();

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  if (isLoading) {
    return (
      <Paper flex={1} component={Box} variant="outlined">
        <Stack p={2} spacing={2}>
          <Skeleton variant="text" width={200} height={28} />
          <Skeleton variant="rectangular" height={120} />
        </Stack>
      </Paper>
    );
  }

  const projectsSorted = projects
    .slice()
    .filter((project) => project.startOn !== null && project.endOn !== null)
    .sort(compareProjects);

  const dateScale = scaleTime({
    range: [40, resizeObserver.width - 40],
    domain: [
      timelineState.interval.start.toJSDate(),
      timelineState.interval.end.toJSDate(),
    ],
  });

  const hiddenProjectCount = projectsSorted.reduce((count, project) => {
    if (project.startOn === null || project.endOn === null) {
      return count + 1;
    }

    return count;
  }, 0);

  const hasHiddenProjects = hiddenProjectCount > 0;
  const projectCount = projectsSorted.length - hiddenProjectCount;
  const height = Math.max(
    resizeObserver.height - AXIS_HEIGHT,
    BAR_MARGIN + BAR_HEIGHT * projectCount + BAR_MARGIN * projectCount
  );

  const updateTooltip = (event: MouseEvent<SVGElement>) => {
    if (svgRef.current === null) {
      return;
    }

    const point = localPoint(svgRef.current, event);

    if (point === null) {
      return;
    }

    const step = BAR_HEIGHT + BAR_MARGIN;
    const index = Math.floor(point.y / step);
    const project = projectsSorted[index];

    showTooltip({
      tooltipTop: step * index,
      tooltipLeft: point.x,
      tooltipData: project,
    });
  };

  const handleMouseMove: MouseEventHandler<SVGElement> = (event) => {
    updateTooltip(event);
  };

  const handleMouseLeave = () => {
    hideTooltip();
  };

  const handleContainerRef = (element: SVGElement | null) => {
    svgRef.current = element;

    containerRef(element);
  };

  const handleClickScroll = () => {
    if (scrollIndicator.element === null) {
      return;
    }

    scrollIndicator.element.scrollTo({
      top: scrollIndicator.element.scrollHeight,
      behavior: 'smooth',
    });

    scrollIndicator.hide();
  };

  return (
    <>
      <Collapse in={hasHiddenProjects}>
        <Alert severity="info" sx={{ marginBottom: 2 }}>
          {pluralize(hiddenProjectCount, { singular: 'project' })} without a
          date range {hiddenProjectCount === 1 ? 'is' : 'are'} hidden
        </Alert>
      </Collapse>
      <Paper
        flex={1}
        component={Box}
        display="flex"
        variant="outlined"
        overflow="hidden"
        flexDirection="column"
        sx={{ height: '100%' }}
      >
        <Stack direction="row" alignItems="center" paddingX={2} paddingY={1}>
          <Typography variant="body2" marginRight="auto">
            {timelineState.interval.toLocaleString({ dateStyle: 'medium' })}
          </Typography>
          <TimelineControls
            density={timelineState.zoom}
            canZoomIn={timelineState.canZoomIn}
            canZoomOut={timelineState.canZoomOut}
            onClickZoomIn={timelineState.zoomIn}
            onClickZoomOut={timelineState.zoomOut}
            onClickForward={timelineState.shiftForward}
            onClickBackward={timelineState.shiftBackward}
          />
        </Stack>
        <Divider />
        <Box
          ref={ref}
          flex={1}
          display="flex"
          overflow="hidden"
          flexDirection="column"
          position="relative"
          sx={{ minHeight: 0 }}
        >
          <svg width="100%" height={AXIS_HEIGHT} style={{ display: 'block' }}>
            <TimelineAxis
              scale={dateScale}
              width={resizeObserver.width}
              density={timelineState.zoom}
              interval={timelineState.interval}
              axisHeight={AXIS_HEIGHT}
              chartHeight={AXIS_HEIGHT}
            />
          </svg>
          <Box ref={scrollIndicator.ref} flex={1} overflow="auto">
            <ScrollIndicator
              label="More projects"
              isVisible={scrollIndicator.isVisible}
              onClick={handleClickScroll}
            />
            <svg
              ref={handleContainerRef}
              width="100%"
              height={height}
              style={{ display: 'block' }}
            >
              <TimelineProvider>
                <TimelineColumns
                  y={0}
                  scale={dateScale}
                  height={height}
                  density={timelineState.zoom}
                  interval={timelineState.interval}
                />
                <TimelineDateIndicator
                  y={0}
                  height={height}
                  scale={dateScale}
                  dateTime={today}
                  color={theme.palette.primary.light}
                />
                {tooltipData && tooltipOpen && (
                  <TooltipInPortal
                    top={tooltipTop}
                    left={tooltipLeft}
                    style={{
                      ...defaultStyles,
                      padding: 0,
                      zIndex: theme.zIndex.tooltip,
                      color: theme.palette.text.primary,
                    }}
                  >
                    <ProjectTimelineTooltip project={tooltipData} />
                  </TooltipInPortal>
                )}
                {projectsSorted.map((project, index) => {
                  if (project.startOn === null || project.endOn === null) {
                    return null;
                  }

                  const y = index * (BAR_HEIGHT + BAR_MARGIN) + BAR_MARGIN;
                  const assignedAssetCount = getAssignedAssetCount(project);
                  const requestedAssetCount = getRequestedAssetCount(project);
                  const label = `${project.name} (${formatNumber(assignedAssetCount)} / ${formatNumber(requestedAssetCount)})`;

                  return (
                    <Link
                      key={project.id}
                      to="/projects/$projectKey"
                      params={{ projectKey: project.key }}
                    >
                      <ProjectTimelineSegment
                        y={y}
                        scale={dateScale}
                        label={label}
                        start={project.startOn}
                        end={project.endOn}
                        height={BAR_HEIGHT}
                        isArchived={project.isArchived}
                        onMouseMove={handleMouseMove}
                        onMouseLeave={handleMouseLeave}
                      />
                    </Link>
                  );
                })}
              </TimelineProvider>
            </svg>
          </Box>
        </Box>
      </Paper>
    </>
  );
}
